/* eslint-disable react/no-array-index-key */
import React, { ReactElement, useEffect } from 'react'
import smoothscroll from 'smoothscroll-polyfill'

import SlideButtons from 'components/UI/SlideButtons/SlideButtons'
import useEffectOnce from 'hooks/useEffectOnce'

import styles from './InfiniteHorizontalScrollLoop.module.css'

type InfiniteHorizontalScrollLoopProps = {
  surroundingBackup: number
  outerStyle?: React.CSSProperties
  innerStyle?: React.CSSProperties
  steps: number
  scrollLength: number
  intervalSeconds?: number
  showSlideButtons?: boolean
  children: ReactElement
}

const InfiniteHorizontalScrollLoop = ({
  surroundingBackup = 4,
  outerStyle,
  innerStyle,
  steps,
  scrollLength,
  intervalSeconds,
  showSlideButtons = true,
  children,
}: InfiniteHorizontalScrollLoopProps): JSX.Element => {
  const contentRef = React.useRef<HTMLDivElement | null>(null)
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const [width, setWidth] = React.useState<number>(0)

  const backupWidth = width * surroundingBackup

  useEffectOnce(() => {
    smoothscroll.polyfill()
  })

  const handleScroll = React.useCallback(() => {
    if (scrollRef.current) {
      const scroll = scrollRef.current.scrollLeft
      if (scroll < backupWidth || scroll >= backupWidth + width) {
        scrollRef.current.scrollLeft = backupWidth + (scroll % width)
      }
    }
  }, [width, backupWidth])

  useEffect(() => {
    if (contentRef.current) {
      setWidth(contentRef.current.offsetWidth)
      scrollRef.current.scrollLeft = backupWidth
    }
  }, [backupWidth])

  const onSlideChanged = (step: number) => {
    scrollRef.current.scrollTo({
      top: 0,
      left: step * scrollLength + scrollLength * (steps + 0.5),
      behavior: 'smooth',
    })
  }

  return (
    <>
      <div className={styles.container} style={outerStyle}>
        <div
          className={styles.innerContainer}
          ref={scrollRef}
          style={innerStyle}
          onScroll={handleScroll}
        >
          {Array(surroundingBackup)
            .fill(1)
            .map((_, index) => (
              <div key={index}>{children}</div>
            ))}
          <div ref={contentRef}>{children}</div>
          {Array(surroundingBackup)
            .fill(1)
            .map((_, index) => (
              <div key={index}>{children}</div>
            ))}
        </div>
      </div>

      {showSlideButtons && (
        <SlideButtons
          steps={steps}
          onChange={onSlideChanged}
          intervalSeconds={intervalSeconds}
        />
      )}
    </>
  )
}

export default InfiniteHorizontalScrollLoop
